<template>
  <b-container fluid>
    <h1 class="m-3">
      {{ title }}
    </h1>
    <b-row class="px-3 justify-content-between">
      <b-col cols="12">
        <div class="d-flex flex-wrap flex-lg-nowrap filter-gap">

          <div class="filter-width">
            <filter-site-picker :use-default-value="true" :multiple="true" :select-all="true" operator="equals"
              :item-key="siteIdFilterProperty" :item-label="siteLabelFilterProperty"
              :item-currency="siteCurrencyFilterProperty" :group-by-key="groupKey" :group-by-label="groupLabel"
              @filterWithIdChanged="siteFilterChanged($event)" />
          </div>

          <div class="d-flex filter-gap flex-wrap">
            <date-range-dual-picker :time-range-value="timeRangeValue"
              @dateRangeDualFiltersUpdated="dateRangeDualFiltersUpdated($event)" />

            <time-range-picker :use-default-value="true" @timeRangeFilterChanged="timeRangeFilterUpdated($event)" />
          </div>

          <b-dropdown toggle-class="dropdown-style" right :text="$t('export.export')"
            class="ml-lg-auto align-self-center align-self-lg-start">
            <b-dropdown-item class="mb-2" :disabled="!dataReadyTables" @click="downloadPdf()">
              <font-awesome-icon icon="file-pdf" class="mr-2 export-icon-pdf" />
              {{ $t("export.PDF") }}
            </b-dropdown-item>
            <b-dropdown-item class="mb-2" :disabled="!dataReadyTables" @click="downloadExcel()">
              <font-awesome-icon icon="file-excel" class="mr-2 export-icon-excel" />
              {{ $t("export.excel") }}
            </b-dropdown-item>
            <b-dropdown-item :disabled="!dataReadyTables" @click="downloadCsv()">
              <font-awesome-icon icon="file-csv" class="mr-2 export-icon" />
              {{ $t("export.csv") }}
            </b-dropdown-item>
          </b-dropdown>

        </div>
      </b-col>
    </b-row>
    <table-renderer v-for="data of tablesData" :key="data.tableNumber" :data-table="data.dataTable" ref="tableRenderers"
      :dimension-fields="data.dimensionColumns" :measure-fields="data.measureColumns" :group-headers="groupHeaders"
      :table-header="data.tableHeader" :default-sorted-column="defaultSortedColumnTables" :totals-data="data.totalsData"
      :data-ready="dataReadyTables" custom-table-class="table-width-medium" />
  </b-container>
</template>

<script>
import Tools from "@/utils/tools";
import TableTools from "@/utils/tableTools";
import paths from "@/router/pathConstants";
import TableRenderer from "@/components/TableRenderer.vue";
import mesureEnum from "@/model/mesureEnum";
import dimensionEnum from "@/model/dimensionEnum";
import {
  columnTypeEnum,
  groupKeyEnum,
  calculatedColumnsEnum,
} from "@/model/columnsEnums";
import { dateFormatEnum } from "@/filter/dateFormat";
import FilterSitePicker from "@/components/picker/FilterSitePicker.vue";
import DateRangeDualPicker from "@/components/picker/DateRangeDualPicker.vue";
import TimeRangePicker from "@/components/picker/TimeRangePicker.vue";
import { KioskCompare } from "@/router/routeNames";
import abortMixin from "@/mixins/abortMixin";

export default {
  name: KioskCompare,
  mixins: [abortMixin],
  components: {
    FilterSitePicker,
    DateRangeDualPicker,
    TimeRangePicker,
    TableRenderer,
  },
  data() {
    return {
      /** Tableau d'objets des sites sélectionnés, indiquant leur id et leur label */
      siteValues: [],
      /** Filtre venant du FilterPicker, tel qu'attendu par cubeJs.
       * Ex :
       *    {
       *      dimension: "Site.label",
       *      operator: "equals",
       *      values: ["Site 1"]
       *    }
       */
      siteFilter: null,
      siteIdFilterProperty: dimensionEnum.SITE,
      siteLabelFilterProperty: dimensionEnum.SITE_NAME,
      siteCurrencyFilterProperty: dimensionEnum.SITE_CURRENCY,
      groupKey: dimensionEnum.SITE_SITE_CATEGORY,
      groupLabel: dimensionEnum.SITE_SITE_CATEGORY_NAME,
      datePickerFormat: dateFormatEnum.DATE_SHORT_FORMAT,
      /** Filtre venant du DateRangePicker, tel qu'attendu par cubeJs.
       * Ex :
       *    {
       *      dimension: "BusinessDate.id",
       *      operator: "inDateRange",
       *      values: ["2020-09-23", "2020-09-26"]
       *    }
       */
      dateRangeFilter1: null,
      dateRangeFilter2: null,
      /** Libellé des dates sélectionnées, telles qu'affichées dans le DateRangePicker */
      dateRangeStr1: "",
      dateRangeStr2: "",
      /** Libellé de la période (dates) sélectionnée */
      labelDatePeriod1: "",
      labelDatePeriod2: "",
      /** Filtre venant du TimeRangePicker, tel qu'attendu par cubeJs.
       * NB : Il faut lister les valeurs possibles au lieu d'utiliser des filtres >,< car cubeJs ne supporte pas encore bien le OR (pour les horaires > 23)
       * Ex :
       *    {
       *      dimension: dimensionEnum.TIME_HOUR,
       *      operator: "equals",
       *      values: ["18", "19", "20", "21", "22", "23", "0", "1"]
       *    }
       */
      timeRangeFilter: null,
      isTimeFullRange: false,
      timeRangeValue: null,
      dataReadyTables: false,
      dimensionColumnsTables: [],
      measureColumnsTables: [],
      defaultSortedColumnTables: "",
      tablesData: [],
    };
  },
  computed: {
    isDefaultQueryReady() {
      return (
        this.siteFilter &&
        this.dateRangeFilter1 &&
        this.dateRangeFilter2 &&
        (this.timeRangeFilter || this.isTimeFullRange)
      );
    },
    title() {
      return this.$t("title.kiosk_compare");
    },
  },
  watch: {
    // TODO custom column header for periods : ça fonctionne en décommentant dateRangeStr1 et dateRangeStr2, mais ça élargi trop les colonnes, à discuter
    // dateRangeStr1(newValue, oldValue) {
    //   const self = this;
    //   self.measureColumnsTables = self.measureColumnsTables.map(col => {
    //     if (col.key == calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD1 || col.key == calculatedColumnsEnum.PERCENT_PER_KIOSK_PERIOD1) {
    //       col.label = newValue;
    //     }
    //     return col;
    //   });
    // },
    // dateRangeStr2(newValue, oldValue) {
    //   const self = this;
    //   self.measureColumnsTables = self.measureColumnsTables.map(col => {
    //     if (col.key == calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD2 || col.key == calculatedColumnsEnum.PERCENT_PER_KIOSK_PERIOD2) {
    //       col.label = newValue;
    //     }
    //     return col;
    //   });
    // }
  },
  mounted() {
    this.initDisplayTables();
  },
  methods: {
    siteFilterChanged(value) {
      this.siteValues = value.values;
      this.siteFilter = value.idFilter;
      this.updateTables();
    },
    dateRangeDualFiltersUpdated(value) {
      if (value && value.dateRangePickerData1 && value.dateRangeFilterData2) {
        this.dateRangeFilter1 = value.dateRangePickerData1.dateRangeFilter;
        this.dateRangeStr1 = value.dateRangePickerData1.dateRangeStr;
        this.labelDatePeriod1 = value.dateRangePickerData1.labelPeriod;
        this.dateRangeFilter2 = value.dateRangeFilterData2.dateRangeFilter;
        this.dateRangeStr2 = value.dateRangeFilterData2.dateRangeStr;
        this.labelDatePeriod2 = value.dateRangeFilterData2.labelPeriod;
      } else {
        this.dateRangeFilter1 = null;
        this.dateRangeStr1 = "";
        this.labelDatePeriod1 = "";
        this.dateRangeFilter2 = null;
        this.dateRangeStr2 = "";
        this.labelDatePeriod2 = "";
      }
      this.updateTables();
    },
    timeRangeFilterUpdated(value) {
      if (value) {
        this.timeRangeValue = value;
        this.timeRangeFilter = value.timeRangeFilter;
        this.isTimeFullRange = value.isFullRange;
      } else {
        this.timeRangeValue = null;
        this.timeRangeFilter = null;
        this.isTimeFullRange = false;
      }
      this.updateTables();
    },
    updateTables() {
      if (this.isDefaultQueryReady) {
        let commonFilters = [this.siteFilter];
        if (!this.isTimeFullRange) {
          commonFilters.push(this.timeRangeFilter);
        }
        const dimensions = [
          dimensionEnum.KIOSK,
          dimensionEnum.KIOSK_NAME,
          dimensionEnum.SITE,
          dimensionEnum.SITE_NAME,
          dimensionEnum.SITE_CURRENCY,
        ];
        this.updateTableList(
          commonFilters,
          this.dateRangeFilter1,
          this.dateRangeFilter2,
          dimensions
        );
      } else {
        this.dataReadyTables = false;
      }
    },
    async updateTableList(
      commonFilters,
      dateRangeFilter1,
      dateRangeFilter2,
      dimensions
    ) {
      try {
        const { signal } = this.resetAbortController();
        var self = this;
        self.dataReadyTables = false;

        self.tablesData = [];
        // Initialisation des objets contenant toutes les données pour chaque site :
        // permet d'afficher dès maintenant les entêtes de tableaux et le loader pour indiquer que les requêtes sont en cours
        self.siteValues.forEach((site, i) => {
          var table = {
            tableNumber: i.toString(),
            tableHeader: site.label,
            tableHeaderId: site.id,
            dataTable: [],
            totalsData: {},
          };
          self.tablesData.push(table);
        });

        const birthdayFilter = TableTools.Helper.getFilterPurchaseHistoryNotBirthday();
        var queries = [];

        queries.push({
          groupKey: groupKeyEnum.HIDDENGROUP_PERIOD1,
          query: {
            measures: [
              mesureEnum.PURCHASEHISTORY_AMOUNT,
            ],
            dimensions: dimensions,
            timeDimensions: dateRangeFilter1 ? [dateRangeFilter1] : [],
            filters: [...commonFilters, birthdayFilter],
          },
        });
        queries.push({
          groupKey: groupKeyEnum.HIDDENGROUP_PERIOD1,
          query: {
            measures: [
              mesureEnum.SNACKHISTORY_AMOUNT_CB_TTC,
            ],
            dimensions: dimensions,
            timeDimensions: dateRangeFilter1 ? [dateRangeFilter1] : [],
            filters: [...commonFilters],
          },
        });

        // Récupération de tous les kiosques même s'ils n'ont pas eu d'activité
        queries.push({
          groupKey: groupKeyEnum.HIDDENGROUP_PERIOD1,
          query: {
            measures: [],
            dimensions: dimensions,
            filters: [
              self.siteFilter,
              TableTools.Helper.getFilterKioskNotDeleted(),
            ],
          },
        });

        queries.push({
          groupKey: groupKeyEnum.HIDDENGROUP_PERIOD2,
          query: {
            measures: [
              mesureEnum.PURCHASEHISTORY_AMOUNT,
            ],
            dimensions: dimensions,
            timeDimensions: dateRangeFilter2 ? [dateRangeFilter2] : [],
            filters: [...commonFilters, birthdayFilter],
          },
        });
        queries.push({
          groupKey: groupKeyEnum.HIDDENGROUP_PERIOD2,
          query: {
            measures: [
              mesureEnum.SNACKHISTORY_AMOUNT_CB_TTC,
            ],
            dimensions: dimensions,
            timeDimensions: dateRangeFilter2 ? [dateRangeFilter2] : [],
            filters: [...commonFilters],
          },
        });

        var resultSets = await TableTools.Helper.doMultipleRequest(queries, { signal });
        var mergedResults = TableTools.Helper.mergeResultsByCommonDimensions(
          resultSets,
          dimensions
        );

        // Extraire les devises par site depuis les résultats
        const siteCurrencies = {};
        mergedResults.forEach(result => {
          const siteId = result[dimensionEnum.SITE];
          if (siteId && result[dimensionEnum.SITE_CURRENCY] && !siteCurrencies[siteId]) {
            siteCurrencies[siteId] = result[dimensionEnum.SITE_CURRENCY];
          }
        });

        // Assigner les devises aux tables
        self.tablesData.forEach(table => {
          table.siteCurrency = siteCurrencies[table.tableHeaderId] || 'EUR';
        });

        // Complétion des objets avec les données reçues pour chaque site
        self.siteValues.forEach((site) => {
          var table = self.tablesData.find(
            (tab) => tab.tableHeaderId === site.id
          );
          const tableSiteCurrency = table.siteCurrency || 'EUR';

          table.dataTable = mergedResults.filter(
            (result) => result[dimensionEnum.SITE] === site.id
          );
          // Ajouter la devise à chaque ligne pour le formatage des totaux
          table.dataTable.forEach(row => {
            row["Site.currency"] = tableSiteCurrency;
          });
          table.dataTable = self.completeInvoiceGroup(table.dataTable);

          // Créer les colonnes spécifiques pour ce tableau avec sa devise
          table.dimensionColumns = TableTools.Helper.constructColumnTable(
            [dimensionEnum.KIOSK_NAME],
            null,
            columnTypeEnum.DIMENSION,
            true,
            null
          );
          table.measureColumns = TableTools.Helper.constructColumnTable(
            [
              calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD1,
              calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD2,
              calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS,
            ],
            null,
            columnTypeEnum.MEASURE,
            true,
            null,
            false,
            tableSiteCurrency
          );

          table.measureColumns = table.measureColumns.map((col) => {
            if (col.key == calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS) {
              col.tdClass = "col-align-left";
            }
            // Modification des labels de périodes
            if (col.key == calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD1) {
              col.label = TableTools.Helper.formatDateLabel(dateRangeFilter1.dateRange);
            }
            else if (col.key == calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD2) {
              col.label = TableTools.Helper.formatDateLabel(dateRangeFilter2.dateRange);
            }
            return col;
          });

          table.totalsData = TableTools.Helper.calculateTotalsData(
            table.dataTable,
            [
              calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD1,
              calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD2,
            ]
          );
          table.totalsData = self.completeTotals(table.totalsData);
        });

        self.dataReadyTables = true;
      } catch (error) {
        if (this.isAbortError(error)) return;
        Tools.Alerts.Error(this.$swal, error);
      }
    },
    /** Initialise ce qui concerne l'affichage commun à tous les tableaux de ce rapport : quelles colonnes doivent réellement être affichées, dans quel ordre
     */
    initDisplayTables(siteCurrency = 'EUR') {
      const self = this;
      self.dimensionColumnsTables = TableTools.Helper.constructColumnTable(
        [dimensionEnum.KIOSK_NAME],
        null,
        columnTypeEnum.DIMENSION,
        true,
        null
      );
      self.measureColumnsTables = TableTools.Helper.constructColumnTable(
        [
          calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD1,
          calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD2,
          calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS,
        ],
        null,
        columnTypeEnum.MEASURE,
        true,
        null,
        false,
        siteCurrency
      );

      self.measureColumnsTables = self.measureColumnsTables.map((col) => {
        if (
          col.key == calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS
        ) {
          col.tdClass = "col-align-left";
        }
        return col;
      });

      self.groupHeaders = [
        { nbOfMeasures: 3, label: self.$t("group.receipts") },
      ];

      self.defaultSortedColumnTables = dimensionEnum.KIOSK_NAME;
    },
    completeInvoiceGroup(dataTable) {
      return dataTable.map((data) => {
        // Revenue TTC
        const purchaseAmount1 =
          data[
          TableTools.Helper.formatKeyWithGroupKey(
            mesureEnum.PURCHASEHISTORY_AMOUNT,
            groupKeyEnum.HIDDENGROUP_PERIOD1
          )
          ];
        const snackCbAmount1 =
          data[
          TableTools.Helper.formatKeyWithGroupKey(
            mesureEnum.SNACKHISTORY_AMOUNT_CB_TTC,
            groupKeyEnum.HIDDENGROUP_PERIOD1
          )
          ];
        const kioskRevenue1 = (purchaseAmount1 || 0) + (snackCbAmount1 || 0);
        data[calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD1] = kioskRevenue1;

        const purchaseAmount2 =
          data[
          TableTools.Helper.formatKeyWithGroupKey(
            mesureEnum.PURCHASEHISTORY_AMOUNT,
            groupKeyEnum.HIDDENGROUP_PERIOD2
          )
          ];
        const snackCbAmount2 =
          data[
          TableTools.Helper.formatKeyWithGroupKey(
            mesureEnum.SNACKHISTORY_AMOUNT_CB_TTC,
            groupKeyEnum.HIDDENGROUP_PERIOD2
          )
          ];
        const kioskRevenue2 = (purchaseAmount2 || 0) + (snackCbAmount2 || 0);
        data[calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD2] = kioskRevenue2;

        data[calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS] = kioskRevenue2
          ? (kioskRevenue1 / kioskRevenue2 - 1) * 100
          : this.$t("Display.notSignificant");
        return data;
      });
    },
    completeTotals(totalsData) {
      const totalRevenue1 =
        totalsData[calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD1] || 0;
      const totalRevenue2 =
        totalsData[calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD2] || 0;
      totalsData[calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS] = totalRevenue2
        ? (totalRevenue1 / totalRevenue2 - 1) * 100
        : this.$t("Display.notSignificant");

      totalsData["column-class"] = {};
      totalsData["column-class"][
        calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS
      ] = "col-align-left";

      return this.addProgressionStyles(totalsData);
    },
    addProgressionStyles(obj) {
      var cellVariants = {};

      const invoiceProgression =
        obj[calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS];
      if (invoiceProgression && !isNaN(invoiceProgression)) {
        cellVariants[calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS] =
          invoiceProgression > 0 ? "success" : "danger";
      }

      obj["_cellVariants"] = cellVariants;
      return obj;
    },
    downloadPdf() {
      if (this.dataReadyTables) {
        this.storeExportData();
        window.open(paths.EXPORT_PDF, "_blank");
      }
    },
    downloadExcel() {
      if (this.dataReadyTables) {
        this.storeExportData();
        window.open(paths.EXPORT_EXCEL, "_blank");
      }
    },
    downloadCsv() {
      if (this.dataReadyTables) {
        this.storeExportData();
        window.open(paths.EXPORT_CSV, "_blank");
      }
    },
    storeExportData() {
      const self = this;
      var datasForExport = [];

      self.tablesData.forEach((table, index) => {
        const renderer = self.$refs.tableRenderers?.[index];
        const sortKey = renderer?.sortedColumn || self.defaultSortedColumnTables;

        const data = TableTools.Helper.assembleExportDataTable(
          table.dataTable,
          table.dimensionColumns,
          table.measureColumns,
          self.groupHeaders,
          table.tableHeader,
          sortKey,
          null,
          table.totalsData
        );
        datasForExport.push(data);
      });

      const siteLabels = self.siteValues.map((site) => site.label);

      TableTools.Helper.storeExportData(
        datasForExport,
        self.title,
        Tools.StringHelper.constructExportSubtitles(
          siteLabels,
          self.labelDatePeriod1,
          "",
          self.labelDatePeriod2
        ),
        Tools.StringHelper.constructExportFilename(
          self.title,
          null,
          self.dateRangeStr1
        )
      );
    },
  },
};
</script>
